import type {
    ViewStyle
} from "react-native";
import type {
    ScrollAreaPhysicalOffsetConstantType,
    ScrollAreaBarVisibleConstantType,
    ScrollAreaEdgesConstantType,
    ScrollAreaFocusableNodeType,
    ScrollAreaMetrics,
    ScrollAreaEdges
} from "./type";

export const SCROLL_AREA_EDGE_EPSILON = 1;

export const SCROLL_AREA_RADIUS_PATTERN = /Radius$|^cornerShape$/;

export const SCROLL_AREA_TABBABLE_SELECTOR = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe, summary, [contenteditable], [tabindex]:not([tabindex=\"-1\"])";

export const SCROLL_AREA_EMPTY_METRICS: ScrollAreaMetrics = {
    contentHeight: 0,
    contentWidth: 0,
    layoutHeight: 0,
    layoutWidth: 0
};

export const getScrollAreaMaxOffset = (contentLength: number, layoutLength: number): number => {
    return Math.max(0, contentLength - layoutLength);
};

export const getScrollAreaPhysicalOffset = ({
    direction,
    metrics,
    offset,
    isWeb
}: ScrollAreaPhysicalOffsetConstantType): number => {
    if(direction === "rtl" && isWeb) {
        return offset + getScrollAreaMaxOffset(metrics.contentWidth, metrics.layoutWidth);
    }

    return offset;
};

export const getScrollAreaRawOffset = ({
    direction,
    metrics,
    offset,
    isWeb
}: ScrollAreaPhysicalOffsetConstantType): number => {
    if(direction === "rtl" && isWeb) {
        return offset - getScrollAreaMaxOffset(metrics.contentWidth, metrics.layoutWidth);
    }

    return offset;
};

export const getScrollAreaEdges = ({
    direction,
    metrics,
    offsets,
    isWeb
}: ScrollAreaEdgesConstantType): ScrollAreaEdges => {
    const maxX = getScrollAreaMaxOffset(metrics.contentWidth, metrics.layoutWidth);

    const maxY = getScrollAreaMaxOffset(metrics.contentHeight, metrics.layoutHeight);

    const left = getScrollAreaPhysicalOffset({
        offset: offsets.x,
        direction,
        metrics,
        isWeb
    });

    const isLeft = maxX > SCROLL_AREA_EDGE_EPSILON && left > SCROLL_AREA_EDGE_EPSILON;

    const isRight = maxX > SCROLL_AREA_EDGE_EPSILON && maxX - left > SCROLL_AREA_EDGE_EPSILON;

    return {
        isBottom: maxY > SCROLL_AREA_EDGE_EPSILON && maxY - offsets.y > SCROLL_AREA_EDGE_EPSILON,
        isTop: maxY > SCROLL_AREA_EDGE_EPSILON && offsets.y > SCROLL_AREA_EDGE_EPSILON,
        isStart: direction === "rtl" ? isRight : isLeft,
        isEnd: direction === "rtl" ? isLeft : isRight
    };
};

export const getIsScrollAreaBarVisible = ({
    isScrolling,
    isHovered,
    type
}: ScrollAreaBarVisibleConstantType): boolean => {
    if(type === "always" || type === "auto") {
        return true;
    }

    if(type === "hover") {
        return isHovered || isScrolling;
    }

    return isScrolling;
};

export const getIsSameScrollAreaEdges = (current: ScrollAreaEdges, next: ScrollAreaEdges): boolean => {
    return current.isBottom === next.isBottom && current.isStart === next.isStart && current.isEnd === next.isEnd && current.isTop === next.isTop;
};

export const getIsSameScrollAreaMetrics = (current: ScrollAreaMetrics, next: ScrollAreaMetrics): boolean => {
    return current.contentHeight === next.contentHeight && current.contentWidth === next.contentWidth && current.layoutHeight === next.layoutHeight && current.layoutWidth === next.layoutWidth;
};

const isScrollAreaFocusableNode = (value: unknown): value is ScrollAreaFocusableNodeType => {
    return typeof value === "object" && value !== null && "querySelector" in value && typeof value.querySelector === "function";
};

export const getScrollAreaRadiusStyle = (style: ViewStyle | undefined): ViewStyle => {
    const radiusStyle: ViewStyle = {};

    if(!style) {
        return radiusStyle;
    }

    (Object.keys(style) as Array<keyof ViewStyle>).forEach((key) => {
        if(!SCROLL_AREA_RADIUS_PATTERN.test(key)) {
            return;
        }

        Object.assign(radiusStyle, {
            [key]: style[key]
        });
    });

    return radiusStyle;
};

export const getHasScrollAreaFocusableContent = (node: unknown): boolean => {
    if(!isScrollAreaFocusableNode(node)) {
        return false;
    }

    return !!node.querySelector(SCROLL_AREA_TABBABLE_SELECTOR);
};
